import React, { ChangeEvent, ReactNode, useState } from 'react';  
// CSS  
import styles from './TitledField.module.css';  
import layout from '../../../Modules/layout.module.css';  
import effects from '../../../Modules/effects.module.css';  
import Checkbox from '../Checkbox/Checkbox';  
import { SpecialField } from '../../../Types/Products/IDynamicField';
import DropDownList from '../DropDownList/DropDownList';
import { IDropDownListItem } from '../../../Types/DropDownList/IDropDownListItem';
import { AdditionalInfoFieldType } from '../../../Types/AdditionalInformation/AdditionalInfoField';
import OptionsEditorPopup from '../OptionsEditorPopup/OptionsEditorPopup';
  
type TitledFieldProps = {  
  title: string;  
  text: string;  
  poweredByAI?: boolean;  
  children?: ReactNode;  
  valueChanged?: (text: string) => void;  
  titleChanged?: (title: string) => void;  
  onSelectedHandler?: (selected: boolean) => void;  
  locked?: boolean;
  onLockToggle?: () => void;
  fieldType?: string;
  specialField?: SpecialField;
  selected?: boolean;
  // Additional Information field props
  additionalFieldType?: AdditionalInfoFieldType;
  additionalFieldOptions?: string[];
  additionalFieldValue?: string;
  onAdditionalValueChange?: (value: string) => void;
  onOptionsChange?: (options: string[]) => void;
};  
  
const TitledField = ({  
  title,  
  text,  
  poweredByAI,  
  children,  
  valueChanged,  
  titleChanged,  
  onSelectedHandler,  
  locked,
  onLockToggle,
  fieldType,
  specialField,
  selected,
  // Additional Information field props
  additionalFieldType,
  additionalFieldOptions,
  additionalFieldValue,
  onAdditionalValueChange,
  onOptionsChange
}: TitledFieldProps) => {  
  const [isEditingOptions, setIsEditingOptions] = useState(false);

  // Handle the change event and extract the text value  
  const handleTextChange = (event: ChangeEvent<HTMLTextAreaElement>) => {  
    if (valueChanged) {  
      valueChanged(event.target.value);  
    }  
  };  
  
  // Handle the change event for the title input  
  const handleTitleChange = (event: ChangeEvent<HTMLInputElement>) => {  
    if (titleChanged) {  
      titleChanged(event.target.value);  
    }  
  };

  // Render additional information field content
  const renderAdditionalFieldContent = () => {
    if (additionalFieldType === undefined) return null;

    switch (additionalFieldType) {
      case AdditionalInfoFieldType.TextArea:
        console.log('Rendering TextArea with value:', additionalFieldValue, 'text:', text);
        return (
          <div className={styles.additionalFieldContent}>
            <textarea
              placeholder="Enter text..."
              value={additionalFieldValue ?? ''}
              onChange={(e) => {
                console.log('Textarea onChange fired:', e.target.value);
                if (onAdditionalValueChange) {
                  console.log('Calling onAdditionalValueChange');
                  onAdditionalValueChange(e.target.value);
                } else {
                  console.log('onAdditionalValueChange is undefined!');
                }
              }}
              className={styles.additionalTextarea}
              disabled={false}
            />
          </div>
        );

      case AdditionalInfoFieldType.DatePicker:
        return (
          <div className={styles.additionalFieldContent}>
            <input
              type="date"
              value={additionalFieldValue || text || ''}
              onChange={(e) => onAdditionalValueChange?.(e.target.value)}
              className={styles.dateInput}
            />
          </div>
        );

      case AdditionalInfoFieldType.TimePicker:
        return (
          <div className={styles.additionalFieldContent}>
            <input
              type="time"
              value={additionalFieldValue || text || ''}
              onChange={(e) => onAdditionalValueChange?.(e.target.value)}
              className={styles.dateInput}
            />
          </div>
        );

      case AdditionalInfoFieldType.DropDownList:
        if (!additionalFieldOptions || additionalFieldOptions.length === 0) {
          return (
            <div className={styles.additionalFieldContent}>
              <p className={styles.noOptionsMessage}>No options defined yet</p>
              <button 
                onClick={() => setIsEditingOptions(true)} 
                className={styles.editOptionsButton}
              >
                Define Options
              </button>
            </div>
          );
        }
        
        const dropdownOptions: IDropDownListItem[] = additionalFieldOptions.map((option, index) => ({
          value: index.toString(),
          text: option,
          colour: undefined,
          textColour: undefined
        }));

        return (
          <div className={styles.additionalFieldContent}>
            <DropDownList
              value=""
              placeholder="Select option"
              data={dropdownOptions}
              onChange={(item) => onAdditionalValueChange?.(item.text)}
            />
            <button 
              onClick={() => setIsEditingOptions(true)} 
              className={styles.editOptionsButton}
            >
              Edit Options
            </button>
          </div>
        );

      case AdditionalInfoFieldType.MultiSelect:
        if (!additionalFieldOptions || additionalFieldOptions.length === 0) {
          return (
            <div className={styles.additionalFieldContent}>
              <p className={styles.noOptionsMessage}>No options defined yet</p>
              <button 
                onClick={() => setIsEditingOptions(true)} 
                className={styles.editOptionsButton}
              >
                Define Options
              </button>
            </div>
          );
        }

        return (
          <div className={styles.additionalFieldContent}>
            <div className={styles.multiSelectPreview}>
              {additionalFieldOptions.map((option, index) => (
                <label key={index} className={styles.multiSelectChip}>
                  <input
                    type="checkbox"
                    name={`multiselect-${title}-${index}`}
                    value={option}
                    onChange={(e) => onAdditionalValueChange?.(e.target.value)}
                    className={styles.multiSelectInput}
                  />
                  <span className={styles.multiSelectCheckmark}>✓</span>
                  <span className={styles.multiSelectChipLabel}>{option}</span>
                </label>
              ))}
            </div>
            <button 
              onClick={() => setIsEditingOptions(true)} 
              className={styles.editOptionsButton}
            >
              Edit Options
            </button>
          </div>
        );

      case AdditionalInfoFieldType.Switch:
        return (
          <div className={styles.additionalFieldContent}>
            <label className={styles.checkboxOption}>
              <input
                type="checkbox"
                onChange={(e) => onAdditionalValueChange?.(e.target.checked.toString())}
              />
              <span>Switch</span>
            </label>
          </div>
        );

      default:
        return null;
    }
  };  
  
  return (  
    <div className={`${layout.column} ${layout.relative} ${styles.card}`}>  
      <div className={styles.header}>  
        {onSelectedHandler && <Checkbox value={selected} disabled={locked ?? false} clickHandler={onSelectedHandler}>
          {locked && <img src='\img\Resources\lock.png' alt="locked" className={styles.lock}/>}
        </Checkbox>}  
        <div className={styles.headerMain}>
          <input  
            className={styles.titleInput}  
            value={title}  
            placeholder={"Enter title..."}  
            onChange={handleTitleChange}
            disabled={locked ?? false}
          />  
          {onLockToggle && (
            <button 
              onClick={onLockToggle}
              className={styles.lockButton}
              title={locked ? "Unlock" : "Lock"}
            >
              <img 
                src="/img/Resources/lock.png"
                alt={locked ? "locked" : "unlocked"} 
                className={`${styles.lockIcon} ${locked ? styles.locked : styles.unlocked}`}
              />
            </button>
          )}
          {locked && !onLockToggle && (
            <button 
              className={styles.permanentLockButton}
              title="Permanently locked"
              disabled={true}
            >
              <img 
                src="/img/Resources/lock.png"
                alt="permanently locked" 
                className={`${styles.lockIcon} ${styles.permanentLocked}`}
              />
            </button>
          )}
          <div>
            {fieldType && <div className={styles.fieldTypeBadge}>{fieldType}</div>}
          </div>
          {/*<div>
            {fieldType && <div className={styles.title}>{specialField}</div>}
          </div>*/}
        </div>
        
        
        <p className={layout.flex1}></p>  
      </div>  
      <div className={styles.content}>  
        {additionalFieldType !== undefined ? renderAdditionalFieldContent() : (children ?? <textarea className={styles.textarea} onChange={handleTextChange} value={text} placeholder='Enter field description...' disabled={locked ?? false}/>)}  
      </div>

      <OptionsEditorPopup
        isVisible={isEditingOptions && additionalFieldType !== undefined}
        title={title}
        options={additionalFieldOptions || []}
        onClose={() => setIsEditingOptions(false)}
        onSave={(options) => onOptionsChange?.(options)}
        requireAtLeastOneOption={true}
      />
    </div>  
  );  
};  
  
export default TitledField;  
